import ActionButton from "../ActionButton";
import { useState, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import { faDownload, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { KEY_NAME } from "../../../utils/KeyName";
import "./GraphDownloadButton.scss";

const d3ToPng = require("d3-svg-to-png");

const headerMap = {
  id: "id",
  agencyId: "Funder ID",
  label: "Record",
  countDetails: "Record Count",
  countDetails_value: "Record Count",
  countDetails_percentage: "Record Count Percentage",
  name: "Name",
  workCount: "Total Count",
  isChorusMember: "Is Chorus Member",
  value: "Value",
  percentage: "Percentage",
  funder: "Funder",
  publiclyAccessibleYes: "Publicly Accessible - Yes",
  publiclyAccessibleUnknown: "Publicly Accessible - Unknown",
  licenseYes: "License - Yes",
  licenseUnknown: "License - Unknown",
  archiveYes: "Archive - Yes",
  archiveUnknown: "Archive - Unknown",
  onFunderPortal: "On Funder Portal",
  publisher: "Publisher",
};

const HEADER_MAP_FOR_TOP_PUBLISHER_BY_CHORUS = {
  // id: "Member ID",
  rorId: "ROR ID",
  name: "Name",
  workCount: "Publications",
  isChorusMember: "IsCHORUSParticipant",
};

const HEADER_MAP_FOR_DATASET_REPOSITORIES = {
  name: "Repository",
  workCount: "Datasets",
};

export function downloadExcel(jsonData, fileName, entityType) {
  if (!jsonData?.length) return;

  let HEADER_MAPPING;

  if (
    window.location.pathname.includes("publication-analytics") &&
    fileName.toLowerCase().includes("top")
  ) {
    if (entityType !== KEY_NAME.FUNDER) {
      const { id, ...HEADER_MAP } = HEADER_MAP_FOR_TOP_PUBLISHER_BY_CHORUS;
      HEADER_MAPPING = HEADER_MAP;
    } else {
      HEADER_MAPPING = HEADER_MAP_FOR_TOP_PUBLISHER_BY_CHORUS;
    }
  } else if (
    window.location.pathname.includes("dataset-analytics") &&
    fileName.toLowerCase().includes("repositories")
  ) {
    HEADER_MAPPING = HEADER_MAP_FOR_DATASET_REPOSITORIES;
  } else {
    HEADER_MAPPING = headerMap;
  }

  const isPlainObject = (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value);

  let formattedData = [];

  // For Nested object
  if (jsonData[0].values && Array.isArray(jsonData[0].values)) {
    const allDates = [
      ...new Set(jsonData.flatMap((item) => item.values.map((v) => v.date))),
    ];

    formattedData = allDates.map((date) => {
      const row = { Date: date };

      jsonData.forEach((item) => {
        const found = item.values.find((v) => v.date === date);
        row[item.label] = found ? found.value : "";
      });

      return row;
    });
  } else if (
    jsonData[0].countDetails !== null &&
    typeof jsonData[0].countDetails === "object" &&
    !Array.isArray(jsonData[0].countDetails)
  ) {
    formattedData = jsonData.map((d) => {
      const result = {};

      Object.entries(d).forEach(([key, value]) => {
        if (key !== "activeValue") {
          if (isPlainObject(value)) {
            Object.entries(value).forEach(([subKey, subValue]) => {
              const finalKey =
                headerMap[`${key}_${subKey}`] || `${key}_${subKey}`;
              result[finalKey] = subValue;
            });
          } else {
            const finalKey = headerMap[key] || key;
            result[finalKey] = value;
          }
        }
      });

      return result;
    });
  }
  //For Flat objects
  else {
    formattedData = jsonData.map(({ activeValue, ...rest }) => {
      const row = {};

      Object.entries(rest).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          !(typeof value === "string" && value.trim() === "")
        ) {
          const finalKey = HEADER_MAPPING?.[key];
          if (finalKey) {
            row[finalKey] =
              typeof value === "string" && value?.startsWith("*")
                ? value.slice(1)
                : value;
          }
        }
      });

      return row;
    });
  }

  const worksheet = XLSX.utils.json_to_sheet(formattedData);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

function GraphDownloadButton({
  svg,
  fileName,
  jsonData,
  btnTooltopMessage = "",
}) {
  const [boolLoader, setBoolLoader] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { entityType } = useParams();

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handler);
    }
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  function downloadPNG() {
    setDropdownOpen(false);
    setBoolLoader(true);
    d3ToPng(svg, fileName, {
      format: "png",
      scale: 5,
      // scale: Math.max(window.devicePixelRatio || 1, 2),
      download: true,
      background: "white",
    }).then(() => {
      setBoolLoader(false);
    });
  }

  // SVG geometry/path attributes that Blink also exposes as CSS properties.
  // Their computed values must NOT be inlined: svg2pdf.js resolves every
  // attribute from `element.style` *before* falling back to the real
  // presentation attribute, and the CSS serialization is not a valid
  // attribute value — computed `d` comes back as `path("M0,0 H...Z")`, which
  // its path parser rejects, so every <path> (the bars, the axis line)
  // silently renders as nothing. Leaving these out lets svg2pdf read the
  // clean attributes d3 already set.
  const GEOMETRY_PROPS = new Set([
    "d",
    "x",
    "y",
    "width",
    "height",
    "rx",
    "ry",
    "cx",
    "cy",
    "r",
  ]);

  function inlineComputedStyles(source, target) {
    const computed = window.getComputedStyle(source);
    for (const key of computed) {
      if (GEOMETRY_PROPS.has(key)) continue;
      // svg2pdf.js has no access to the app's web fonts and falls back to a
      // built-in Helvetica-equivalent. Pin font-family to a metric-compatible
      // font here so the text width it measures (for centering/anchoring)
      // matches what it actually draws — otherwise labels can end up
      // off-center depending on which system font the browser resolves the
      // app's font stack to.
      target.style[key] =
        key === "font-family" ? "Arial, Helvetica, sans-serif" : computed[key];
    }
    for (let i = 0; i < source.children.length; i++) {
      inlineComputedStyles(source.children[i], target.children[i]);
    }
  }

  // Resolve an SVG length ("0.32em", "-9", "4px") to px against `fontSize`.
  function toPx(value, fontSize) {
    if (value === null || value === undefined || value === "") return 0;
    const v = String(value).trim();
    const n = parseFloat(v);
    if (isNaN(n)) return 0;
    return v.slice(-2) === "em" ? n * fontSize : n;
  }

  // Works around two svg2pdf.js text-positioning bugs, both of which only bite
  // on <text> elements (tspan-level offsets are handled correctly):
  //
  //  1. `dx`/`dy` given in `em` resolve against `pdf.getFontSize()`, which
  //     svg2pdf has already multiplied by the PDF scale factor — so an
  //     `0.32em` offset on 14px text comes out as 3.36px instead of 4.48px.
  //  2. As soon as a <tspan> carries an absolute `x` (wrapText always sets
  //     one), svg2pdf starts a fresh text chunk from that `x` alone and drops
  //     the parent <text>'s `dx` entirely, while still *adding* its `dy` on
  //     top of the tspan's — but per SVG the innermost value wins, so the
  //     browser applies only the tspan's.
  //
  // Fold the parent's `dx` into the first positioned tspan and drop the
  // now-redundant <text> offsets, so the PDF matches the on-screen layout.
  // Must run after inlineComputedStyles(), which supplies `style.fontSize`.
  function normalizeTextOffsets(root) {
    root.querySelectorAll("text").forEach((text) => {
      const fontSize = parseFloat(text.style.fontSize) || 16;
      const dxPx = toPx(text.getAttribute("dx"), fontSize);
      const tspans = text.getElementsByTagName("tspan");

      if (tspans.length > 0) {
        const anchored = Array.from(tspans).find(
          (t) => t.getAttribute("x") !== null,
        );
        if (dxPx && anchored) {
          anchored.setAttribute(
            "x",
            String(toPx(anchored.getAttribute("x"), fontSize) + dxPx),
          );
          text.removeAttribute("dx");
        } else if (dxPx) {
          text.setAttribute("dx", String(dxPx));
        }
        // wrapText already folds the <text> dy into every tspan's own dy.
        text.removeAttribute("dy");
      } else {
        if (text.hasAttribute("dx")) text.setAttribute("dx", String(dxPx));
        if (text.hasAttribute("dy")) {
          text.setAttribute(
            "dy",
            String(toPx(text.getAttribute("dy"), fontSize)),
          );
        }
      }
    });
  }

  async function downloadPDF() {
    setDropdownOpen(false);
    setBoolLoader(true);
    try {
      const { jsPDF } = await import("jspdf");
      const { svg2pdf } = await import("svg2pdf.js");

      // Deep-clone the SVG and inline the computed *presentation* styles on
      // every element (everything except the geometry props above, which must
      // stay as attributes).
      const svgEl = svg.cloneNode(true);
      inlineComputedStyles(svg, svgEl);
      normalizeTextOffsets(svgEl);

      const bbox = svg.getBoundingClientRect();
      const svgWidth = bbox.width;
      const svgHeight = bbox.height;

      const margin = 12;
      const pdfWidth = svgWidth + margin * 2;
      const pdfHeight = svgHeight + margin * 2;
      const orientation = svgWidth > svgHeight ? "landscape" : "portrait";

      const doc = new jsPDF({
        orientation,
        unit: "px",
        format: [pdfWidth, pdfHeight],
        // Required for "px" to mean actual CSS pixels — without it jsPDF
        // uses an inverted legacy scale factor (96/72 instead of 72/96),
        // making the whole PDF ~1.78x larger than the on-screen chart.
        hotfixes: ["px_scaling"],
      });

      await svg2pdf(svgEl, doc, {
        x: margin,
        y: margin,
        width: svgWidth,
        height: svgHeight,
      });

      doc.save(`${fileName}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      setBoolLoader(false);
    }
  }

  const isDisabled = boolLoader || !jsonData || jsonData.length === 0;

  return (
    <div className="d-flex justify-content-end pe-3">
      <div className="d-flex flex-row gap-3 align-items-center">
        {/* Download Chart dropdown */}
        <div className="chart-download-dropdown" ref={dropdownRef}>
          <button
            className="btn btn-outline-theme action-btn outline chart-download-trigger"
            onClick={() => setDropdownOpen((o) => !o)}
            disabled={isDisabled}
          >
            <FontAwesomeIcon icon={faDownload} className="pe-2" />
            Download Chart
            <FontAwesomeIcon icon={faChevronDown} className="ms-1" size="xs" />
          </button>

          {dropdownOpen && (
            <div className="chart-download-menu">
              <button onClick={downloadPNG}>Download Chart (.png)</button>
              <button onClick={downloadPDF}>Download Chart (.pdf)</button>
            </div>
          )}
        </div>

        <ActionButton
          icon={faDownload}
          label="Download Data (.xlsx)"
          onClick={() => downloadExcel(jsonData, fileName, entityType)}
          btnTooltopMessage={btnTooltopMessage}
          disabled={
            btnTooltopMessage || !jsonData || jsonData.length === 0
              ? true
              : false
          }
        />
      </div>
    </div>
  );
}

export default GraphDownloadButton;
